import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Campaign as CampaignIcon,
  Assessment as AssessmentIcon,
  Share as ShareIcon,
  CalendarMonth as CalendarIcon,
  Image as ImageIcon,
  SmartToy as SmartToyIcon,
  Visibility as VisibilityIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

// Importar componentes necessários
import AIAssistant from '../components/AIAssistant';
import MarketingScheduler from '../components/MarketingScheduler';
import SocialPostScheduler from '../components/SocialPostScheduler';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`marketing-tabpanel-${index}`}
      aria-labelledby={`marketing-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `marketing-tab-${index}`,
    'aria-controls': `marketing-tabpanel-${index}`,
  };
}

// Componente simulado para Analytics
const AnalyticsCharts = ({ data, platform }: { data: any, platform: string }) => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Crescimento de Seguidores</Typography>
            <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body1">Gráfico de crescimento</Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Taxa de Engajamento</Typography>
            <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body1">Gráfico de engajamento</Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Melhores Horários</Typography>
            <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body1">Gráfico de horários</Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Análise de Sentimento</Typography>
            <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body1">Gráfico de sentimento</Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Componente simulado para Calendário de Conteúdo
const ContentCalendar = ({ events, onEdit, onDelete, onApprove }: { events: any[], onEdit: (event: any) => void, onDelete: (id: string) => void, onApprove: (id: string) => void }) => {
  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Calendário de Conteúdo</Typography>
        <Button variant="contained" startIcon={<CalendarIcon />}>
          Novo Agendamento
        </Button>
      </Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body1">Visualização de calendário</Typography>
        </Box>
      </Paper>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>Próximos Posts</Typography>
      <List>
        {[
          { title: 'Post Instagram', date: '10/04/2025', platform: 'Instagram', status: 'Agendado' },
          { title: 'Vídeo Facebook', date: '12/04/2025', platform: 'Facebook', status: 'Rascunho' },
          { title: 'Post LinkedIn', date: '15/04/2025', platform: 'LinkedIn', status: 'Agendado' },
        ].map((post, index) => (
          <ListItem 
            key={index}
            secondaryAction={
              <Box>
                <IconButton edge="end" aria-label="edit" onClick={() => onEdit(post)}>
                  <EditIcon />
                </IconButton>
                <IconButton edge="end" aria-label="delete" onClick={() => onDelete(`post_${index}`)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            }
            sx={{ mb: 1, bgcolor: 'background.paper', borderRadius: 1 }}
          >
            <ListItemIcon>
              <Avatar sx={{ bgcolor: `hsl(${index * 60}, 70%, 50%)` }}>
                {post.platform.charAt(0)}
              </Avatar>
            </ListItemIcon>
            <ListItemText 
              primary={post.title} 
              secondary={`${post.date} - ${post.platform}`} 
            />
            <Chip label={post.status} color={post.status === 'Agendado' ? 'success' : 'default'} size="small" sx={{ mr: 2 }} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

// Componente para Campanhas de Marketing
const MarketingCampaigns = () => {
  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Campanhas de Marketing</Typography>
        <Button variant="contained" startIcon={<AddIcon />}>
          Nova Campanha
        </Button>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Campanha Atual</Typography>
              <Typography variant="subtitle1" color="primary">Lançamento de Novos Imóveis</Typography>
              <Typography variant="body2" paragraph>
                Campanha focada na divulgação dos novos imóveis disponíveis no portfólio.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Chip label="Em andamento" color="success" />
                <Typography variant="body2">Término: 15/05/2025</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Próxima Campanha</Typography>
              <Typography variant="subtitle1" color="primary">Financiamento Facilitado</Typography>
              <Typography variant="body2" paragraph>
                Campanha focada em opções de financiamento para compradores de primeiro imóvel.
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Chip label="Planejada" color="info" />
                <Typography variant="body2">Início: 20/05/2025</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Histórico de Campanhas</Typography>
          <List>
            {[
              { title: 'Black Friday Imobiliária', period: '15/11/2024 - 30/11/2024', status: 'Concluída', results: 'Conversão: 12%' },
              { title: 'Verão de Oportunidades', period: '01/01/2025 - 28/02/2025', status: 'Concluída', results: 'Conversão: 8%' },
            ].map((campaign, index) => (
              <ListItem 
                key={index}
                sx={{ mb: 1, bgcolor: 'background.paper', borderRadius: 1 }}
              >
                <ListItemIcon>
                  <Avatar sx={{ bgcolor: `hsl(${index * 120}, 70%, 50%)` }}>
                    <CampaignIcon />
                  </Avatar>
                </ListItemIcon>
                <ListItemText 
                  primary={campaign.title} 
                  secondary={`${campaign.period} | ${campaign.results}`} 
                />
                <Chip label={campaign.status} color="success" size="small" />
              </ListItem>
            ))}
          </List>
        </Grid>
      </Grid>
    </Box>
  );
};

// Função para gerar dados simulados de analytics
const generateAnalytics = (platform: string) => {
  return {
    followers: {
      data: [1000, 1200, 1350, 1500, 1750, 2000, 2200],
      labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul']
    },
    engagement: {
      data: [5, 5.5, 6, 7, 6.5, 8, 7.5],
      labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul']
    }
  };
};

// Função para obter biblioteca de mídia simulada
const getMediaLibrary = () => {
  return [
    { id: '1', name: 'Imagem 1', type: 'image', url: '/images/1.jpg' },
    { id: '2', name: 'Imagem 2', type: 'image', url: '/images/2.jpg' },
    { id: '3', name: 'Vídeo 1', type: 'video', url: '/videos/1.mp4' },
  ];
};

// Função para obter eventos de calendário simulados
const getContentCalendar = () => {
  return [
    { id: '1', title: 'Post Instagram', start: new Date(2025, 3, 10), end: new Date(2025, 3, 10), status: 'scheduled' },
    { id: '2', title: 'Vídeo Facebook', start: new Date(2025, 3, 12), end: new Date(2025, 3, 12), status: 'draft' },
    { id: '3', title: 'Post LinkedIn', start: new Date(2025, 3, 15), end: new Date(2025, 3, 15), status: 'scheduled' },
  ];
};

const MarketingPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulando carregamento de dados
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const metrics = {
    campaigns: 2,
    activeCampaigns: 1,
    conversionRate: 8.5,
    scheduledPosts: 12
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ width: '100%', mt: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="marketing tabs" variant="scrollable" scrollButtons="auto">
            <Tab label="Agendamento" {...a11yProps(0)} />
            <Tab label="Histórico de Campanhas" {...a11yProps(1)} />
          </Tabs>
        </Box>

        {/* Agendamento */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Agendamento
            </Typography>
          </Box>
          <MarketingScheduler />
        </TabPanel>

        {/* Histórico de Campanhas */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Histórico de Campanhas
            </Typography>
          </Box>
          <SocialPostScheduler showPostForm={false} showPostHistory={true} showPlatformSelector={false} />
        </TabPanel>
      </Box>
    </Container>
  );
};

export default MarketingPage;